/**
 * Componente: Modal de Selección de País — PaisModalSelector
 * Fiel al diseño de /root/Avifacil_Planeacion_proyecto/desing frontend/desing-modal-paises.html
 * - Centrado en pantalla con fondo blur neutro (no morado).
 * - Alimentado por `country-state-city` y `country-flag-icons`.
 * - Búsqueda en tiempo real, banderas oficiales SVG y checkmark de selección.
 */

'use client';

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Country } from 'country-state-city';
import * as Flags from 'country-flag-icons/react/3x2';
import { hasFlag } from 'country-flag-icons';
import styles from './PaisModalSelector.module.css';

export interface PaisItem {
  name: string;        // Nombre en español (ej. "Colombia", "México", "España")
  englishName: string; // Nombre original en country-state-city (ej. "Spain")
  isoCode: string;     // Código ISO (ej. "CO", "MX", "ES")
}

export interface PaisModalSelectorProps {
  isOpen: boolean;
  selectedCountryName?: string;
  onSelect: (pais: PaisItem) => void;
  onClose: () => void;
}

// Países destacados en el orden exacto del diseño de referencia
const PRIORITY_ISOS = [
  'CO', // Colombia
  'MX', // México
  'AR', // Argentina
  'BR', // Brasil
  'CL', // Chile
  'PE', // Perú
  'ES', // España
  'US', // Estados Unidos
  'CA', // Canadá
  'EC', // Ecuador
  'VE', // Venezuela
  'BO', // Bolivia
  'UY', // Uruguay
  'PY', // Paraguay
  'CR', // Costa Rica
  'PA', // Panamá
  'GT', // Guatemala
  'DO', // Rep. Dominicana
];

// Instancia de nombres de países en español nativo
let spanishRegionNames: Intl.DisplayNames | null = null;
try {
  spanishRegionNames = new Intl.DisplayNames(['es'], { type: 'region' });
} catch {
  spanishRegionNames = null;
}

function getSpanishCountryName(isoCode: string, defaultName: string): string {
  if (spanishRegionNames) {
    try {
      const translated = spanishRegionNames.of(isoCode);
      if (translated) return translated;
    } catch {
      // Usar nombre predeterminado
    }
  }
  return defaultName;
}

/**
 * Renderizador de bandera oficial SVG mediante `country-flag-icons`
 */
function CountryFlagIcon({ isoCode }: { isoCode: string }) {
  const upper = (isoCode || '').toUpperCase();
  if (hasFlag(upper)) {
    const FlagComp = (
      Flags as Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>>
    )[upper];
    if (FlagComp) {
      return <FlagComp className="w-full h-full object-cover" />;
    }
  }
  return <span className="text-[11px] leading-none select-none">🌐</span>;
}

export function PaisModalSelector({
  isOpen,
  selectedCountryName,
  onSelect,
  onClose,
}: PaisModalSelectorProps) {
  const [search, setSearch] = useState('');
  const [mounted, setMounted] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Cargar catálogo completo de países ordenado
  const allCountries: PaisItem[] = useMemo(() => {
    const rawList = Country.getAllCountries();
    const list: PaisItem[] = rawList.map((c) => ({
      name: getSpanishCountryName(c.isoCode, c.name),
      englishName: c.name,
      isoCode: c.isoCode,
    }));

    // Ordenar: primero los países prioritarios en su orden de diseño, luego el resto alfabéticamente
    list.sort((a, b) => {
      const idxA = PRIORITY_ISOS.indexOf(a.isoCode);
      const idxB = PRIORITY_ISOS.indexOf(b.isoCode);
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      return a.name.localeCompare(b.name, 'es');
    });

    return list;
  }, []);

  // Filtrado reactivo en tiempo real
  const filteredCountries = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return allCountries;

    return allCountries.filter((c) => {
      return (
        c.name.toLowerCase().includes(query) ||
        c.englishName.toLowerCase().includes(query) ||
        c.isoCode.toLowerCase() === query
      );
    });
  }, [allCountries, search]);

  // Manejo de foco automático al abrir
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Cerrar con tecla Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !mounted) return null;

  const normalizedSelected = (selectedCountryName || '').trim().toLowerCase();

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="country-modal-title"
    >
      <div
        className={styles.modalCard}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Botón Cerrar (X) */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 sm:top-6 sm:right-6 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center transition-colors cursor-pointer focus:outline-none z-10"
          aria-label="Cerrar modal"
        >
          <span className="material-symbols-outlined text-[18px]">close</span>
        </button>

        {/* Encabezado: Icono Degradado y Título */}
        <div className="flex items-center gap-3 pr-10">
          <div className={styles.iconBadge}>
            <span className="material-symbols-outlined text-[22px] sm:text-[26px]">public</span>
          </div>
          <h2
            id="country-modal-title"
            className="text-lg sm:text-2xl font-bold text-gray-900 tracking-tight leading-snug"
          >
            Selecciona o busca tu país
          </h2>
        </div>

        {/* Barra de Búsqueda con Iconos Blindados contra Colapso */}
        <div className={styles.searchContainer}>
          <div className={styles.searchIconWrapper}>
            <svg
              className="w-5 h-5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar país..."
            className={styles.searchInput}
          />
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                searchInputRef.current?.focus();
              }}
              className={styles.clearSearchBtn}
              aria-label="Borrar búsqueda"
              title="Borrar búsqueda"
            >
              <svg
                className="w-3.5 h-3.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Grilla de Países en 2 Columnas con Amplio Padding Interior y Scroll Personalizado */}
        <div
          data-scrollable="true"
          className={`w-full flex-1 ${styles.countryListScroll}`}
        >
          {filteredCountries.length > 0 ? (
            <div className={styles.countryGrid}>
              {filteredCountries.map((country) => {
                const isSelected =
                  normalizedSelected === country.name.toLowerCase() ||
                  normalizedSelected === country.englishName.toLowerCase() ||
                  normalizedSelected === country.isoCode.toLowerCase();

                return (
                  <button
                    key={country.isoCode}
                    type="button"
                    onClick={() => {
                      onSelect(country);
                      onClose();
                    }}
                    className={`${styles.countryButton} ${
                      isSelected ? styles.countryButtonSelected : ''
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0 pr-1 flex-1">
                      <span className={styles.flagBadge}>
                        <CountryFlagIcon isoCode={country.isoCode} />
                      </span>
                      <span
                        className={`text-sm sm:text-[14.5px] truncate ${
                          isSelected ? 'font-bold text-purple-950' : 'font-semibold text-gray-800'
                        }`}
                        title={country.name}
                      >
                        {country.name}
                      </span>
                    </div>

                    {isSelected && (
                      <svg
                        className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-[#9333ea] flex-shrink-0 ml-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2.5"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">
                search_off
              </span>
              <p className="text-sm font-medium">
                No encontramos ningún país que coincida con &quot;{search}&quot;
              </p>
              <button
                type="button"
                onClick={() => setSearch('')}
                className="mt-3 text-xs font-semibold text-[#c25ee8] hover:underline cursor-pointer"
              >
                Ver todos los países
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
